import React from "react";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, useDraggable, useDroppable } from "@dnd-kit/core";
import type { Player, Position } from "../models/Player";
import type { DraftStyle } from "../../draftCompanion/components/boardTypes";
import { BoardCell, CellContent } from "./BoardCell";
import TeamLogo from "./TeamLogo";
import { formatTeamAbbreviation } from "../utils/teamAbbreviation";
import { useFitZoomViewport } from "./useFitZoomViewport";

export type StarterKey = "QB" | "RB" | "WR" | "TE" | "Flex" | "Superflex" | "K" | "DST";
export type StarterConfig = Record<StarterKey, number>;
type SlotInstance = { key: StarterKey; index: number; id: string; label: string };
type TeamOverrideMap = Record<string, string>;
export type TeamOverridesState = Record<number, TeamOverrideMap>;
type SlotAssignments = Record<string, Player | undefined>;
type DraftDirection = "left" | "right";
type CompactLayout = "phone" | "tablet";

const STARTER_ORDER: StarterKey[] = ["QB", "RB", "WR", "TE", "Flex", "Superflex", "K", "DST"];
const STARTER_LABELS: Record<StarterKey, string> = {
  QB: "QB",
  RB: "RB",
  WR: "WR",
  TE: "TE",
  Flex: "Flex",
  Superflex: "Superflex",
  K: "Kicker",
  DST: "Defense",
};

const MOBILE_STARTER_LABELS: Record<StarterKey, string> = {
  QB: "QB",
  RB: "RB",
  WR: "WR",
  TE: "TE",
  Flex: "Flex",
  Superflex: "SF",
  K: "K",
  DST: "DEF",
};

export const DEFAULT_STARTER_CONFIG: StarterConfig = {
  QB: 1,
  RB: 2,
  WR: 2,
  TE: 1,
  Flex: 1,
  Superflex: 0,
  K: 1,
  DST: 1,
};

function isReverseRound(roundIndex: number, style: DraftStyle) {
  if (style === "Regular Draft") return false;
  if (style === "Snake Draft") return roundIndex % 2 === 1;
  return roundIndex === 1 || (roundIndex >= 2 && roundIndex % 2 === 0);
}

function getTeamIndexForPick(
  pickIndex: number,
  teams: number,
  draftStyle: DraftStyle,
  draftSlotTeamIndexes?: Array<number | null>
): number {
  const syncedTeamIndex = draftSlotTeamIndexes?.[pickIndex];
  if (
    typeof syncedTeamIndex === "number" &&
    Number.isFinite(syncedTeamIndex) &&
    syncedTeamIndex >= 0 &&
    syncedTeamIndex < teams
  ) {
    return Math.round(syncedTeamIndex);
  }

  const roundIndex = Math.floor(pickIndex / teams);
  const pickOffset = pickIndex % teams;
  return isReverseRound(roundIndex, draftStyle) ? teams - 1 - pickOffset : pickOffset;
}

function getDraftDirectionForPick(pickIndex: number, teams: number, draftStyle: DraftStyle): DraftDirection | null {
  if (pickIndex < 0 || teams <= 0) return null;
  const roundIndex = Math.floor(pickIndex / teams);
  return isReverseRound(roundIndex, draftStyle) ? "left" : "right";
}

function getNextPickingTeamIndex(
  draftSlots: (string | null)[],
  teams: number,
  draftStyle: DraftStyle,
  draftSlotTeamIndexes?: Array<number | null>
): number | null {
  const nextPickIndex = draftSlots.findIndex((playerId) => playerId === null);
  if (nextPickIndex === -1) return null;
  return getTeamIndexForPick(nextPickIndex, teams, draftStyle, draftSlotTeamIndexes);
}

function getNextDraftDirection(
  draftSlots: (string | null)[],
  teams: number,
  draftStyle: DraftStyle
): DraftDirection | null {
  const nextPickIndex = draftSlots.findIndex((playerId) => playerId === null);
  if (nextPickIndex === -1) return null;
  return getDraftDirectionForPick(nextPickIndex, teams, draftStyle);
}

function canFillSlot(slot: StarterKey, player: Player): boolean {
  switch (slot) {
    case "QB":
    case "RB":
    case "WR":
    case "TE":
    case "K":
    case "DST":
      return player.position === slot;
    case "Flex":
      return player.position === "RB" || player.position === "WR" || player.position === "TE";
    case "Superflex":
      return player.position === "QB" || player.position === "RB" || player.position === "WR" || player.position === "TE";
    default:
      return false;
  }
}

function buildVisibleStarterSlots(starterConfig: StarterConfig): SlotInstance[] {
  return STARTER_ORDER.flatMap((slot) =>
    Array.from({ length: starterConfig[slot] }, (_, slotIndex) => ({
      key: slot,
      index: slotIndex,
      id: `${slot}-${slotIndex}`,
      label: starterConfig[slot] > 1 ? `${STARTER_LABELS[slot]} ${slotIndex + 1}` : STARTER_LABELS[slot],
    }))
  );
}

function buildTeamRoster(
  playerIds: string[],
  playersById: Record<string, Player>,
  starterConfig: StarterConfig,
  teamOverrides: TeamOverrideMap
): {
  slotAssignments: SlotAssignments;
  bench: Player[];
  playerLocation: Record<string, { kind: "slot"; slotId: string } | { kind: "bench" }>;
} {
  const starters = buildVisibleStarterSlots(starterConfig);
  const availablePlayers = playerIds.map((playerId) => playersById[playerId]).filter((player): player is Player => Boolean(player));
  const availableById = new Map(availablePlayers.map((player) => [player.id, player]));
  const usedPlayerIds = new Set<string>();
  const slotAssignments: SlotAssignments = {};
  const playerLocation: Record<string, { kind: "slot"; slotId: string } | { kind: "bench" }> = {};

  for (const slot of starters) {
    const overridePlayerId = teamOverrides[slot.id];
    const overridePlayer = overridePlayerId ? availableById.get(overridePlayerId) : undefined;
    if (!overridePlayer || usedPlayerIds.has(overridePlayer.id) || !canFillSlot(slot.key, overridePlayer)) continue;
    slotAssignments[slot.id] = overridePlayer;
    playerLocation[overridePlayer.id] = { kind: "slot", slotId: slot.id };
    usedPlayerIds.add(overridePlayer.id);
  }

  for (const slot of starters) {
    if (slotAssignments[slot.id]) continue;

    const fallbackPlayer = availablePlayers.find((player) => !usedPlayerIds.has(player.id) && canFillSlot(slot.key, player));
    if (!fallbackPlayer) continue;

    slotAssignments[slot.id] = fallbackPlayer;
    playerLocation[fallbackPlayer.id] = { kind: "slot", slotId: slot.id };
    usedPlayerIds.add(fallbackPlayer.id);
  }

  const bench = availablePlayers.filter((player) => !usedPlayerIds.has(player.id));
  for (const player of bench) {
    playerLocation[player.id] = { kind: "bench" };
  }

  return { slotAssignments, bench, playerLocation };
}

function buildDragId(teamIndex: number, playerId: string) {
  return `team:${teamIndex}:player:${playerId}`;
}

function buildSlotDropId(teamIndex: number, slotId: string) {
  return `team:${teamIndex}:slot:${slotId}`;
}

function buildBenchDropId(teamIndex: number) {
  return `team:${teamIndex}:bench`;
}

function parseDragId(id: string): { teamIndex: number; playerId: string } | null {
  const match = /^team:(\d+):player:(.+)$/.exec(id);
  if (!match) return null;
  return { teamIndex: Number(match[1]), playerId: match[2] };
}

function parseDropId(id: string): { teamIndex: number; slotId?: string; isBench: boolean } | null {
  const slotMatch = /^team:(\d+):slot:(.+)$/.exec(id);
  if (slotMatch) return { teamIndex: Number(slotMatch[1]), slotId: slotMatch[2], isBench: false };

  const benchMatch = /^team:(\d+):bench$/.exec(id);
  if (benchMatch) return { teamIndex: Number(benchMatch[1]), isBench: true };

  return null;
}

function PlayerChip({
  label,
  player,
  posColor,
  highlighted = false,
  draggableId,
  isDragging = false,
  compactMode = false,
  compactLayout = "phone",
}: {
  label: string;
  player?: Player;
  posColor: (pos: Position) => string;
  highlighted?: boolean;
  draggableId?: string;
  isDragging?: boolean;
  compactMode?: boolean;
  compactLayout?: CompactLayout;
}) {
  const bg = player ? posColor(player.position) : "var(--surface-0)";
  const effectiveBg = highlighted ? `color-mix(in srgb, ${bg} 82%, rgba(255, 215, 0, 0.32) 18%)` : bg;
  const displayTeam = React.useMemo(() => formatTeamAbbreviation(player?.team, "FA"), [player?.team]);
  const nameLines = React.useMemo(() => {
    if (!player?.name) return ["", ""];
    const parts = player.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return [parts[0] ?? "", ""];
    const suffixes = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
    const lastPart = parts[parts.length - 1] ?? "";
    if (parts.length >= 3 && suffixes.has(lastPart.toLowerCase())) {
      return [parts.slice(0, -2).join(" "), parts.slice(-2).join(" ")];
    }
    return [parts.slice(0, -1).join(" "), lastPart];
  }, [player?.name]);

  if (compactMode) {
    const isTablet = compactLayout === "tablet";
    const compactWidth = isTablet ? 91 : 72;
    const compactHeight = isTablet ? 64 : 48;
    const compactPadding = isTablet ? 5 : 4;
    const innerInset = isTablet ? 6 : 5;
    const compactRadius = 7;
    const labelFontSize = isTablet ? 7 : 6;
    const teamLogoTop = isTablet ? 10 : 8;
    const teamLogoSize = isTablet ? 16 : 14.0625;
    const metaMarginTop = isTablet ? 9 : 7;
    const metaPaddingRight = isTablet ? 21 : 18;
    const metaFontSize = isTablet ? 8.5 : 7.5;
    const nameMarginTop = isTablet ? 5 : 4;
    const namePaddingRight = isTablet ? 10 : 8;
    const nameFontSize = isTablet ? 9 : 8;
    const nameMinHeight = isTablet ? 16 : 14;
    const emptyFontSize = isTablet ? 22 : 18;

    return (
      <div
        style={{
          boxSizing: "border-box",
          width: compactWidth,
          minWidth: compactWidth,
          height: compactHeight,
          padding: compactPadding,
          position: "relative",
          overflow: "hidden",
          userSelect: "none",
          opacity: isDragging ? 0.82 : 1,
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: innerInset,
            right: 0,
            bottom: innerInset,
            left: 0,
            borderRadius: compactRadius,
            border: highlighted ? "1px solid rgba(255,215,0,0.28)" : "1px solid rgba(255,255,255,0.12)",
            outline: highlighted ? "1px solid rgba(255,215,0,0.14)" : "1px solid rgba(0,0,0,0.18)",
            background: effectiveBg,
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: innerInset,
            right: 0,
            bottom: innerInset,
            left: 0,
            borderRadius: compactRadius,
            background: "rgba(0,0,0,0.1)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", zIndex: 1, height: "100%" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 1,
              fontSize: labelFontSize,
              lineHeight: 1,
              fontWeight: 700,
              color: "rgba(255,255,255,0.84)",
              letterSpacing: -0.1,
              textAlign: "right",
              maxWidth: isTablet ? 38 : 30,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </div>

          {player?.team ? (
            <div
              style={{
                position: "absolute",
                top: teamLogoTop,
                right: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TeamLogo
                team={player.team}
                size={teamLogoSize}
                fallback={<span style={{ fontSize: labelFontSize, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>{displayTeam}</span>}
              />
            </div>
          ) : null}

          {player ? (
            <>
              <div
                style={{
                  marginTop: metaMarginTop,
                  paddingRight: metaPaddingRight,
                  fontSize: metaFontSize,
                  lineHeight: 1,
                  fontWeight: 650,
                  color: "rgba(255,255,255,0.76)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  letterSpacing: -0.1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span>{player.position}</span>
                <span style={{ opacity: 0.5 }}>-</span>
                <span>{displayTeam}</span>
              </div>

              <div
                style={{
                  marginTop: nameMarginTop,
                  paddingRight: namePaddingRight,
                  fontSize: nameFontSize,
                  lineHeight: 1,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.97)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  gap: 0,
                  minHeight: nameMinHeight,
                  overflow: "hidden",
                  letterSpacing: -0.08,
                }}
              >
                <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameLines[0]}</span>
                <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameLines[1]}</span>
              </div>
            </>
          ) : (
            <div
              style={{
                marginTop: metaMarginTop,
                fontSize: emptyFontSize,
                lineHeight: 1,
                fontWeight: 700,
                color: "rgba(255,255,255,0.78)",
              }}
            >
              —
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <BoardCell
      id={draggableId ?? `team-chip:${label}`}
      drafted={false}
      dimWhenDrafted={false}
      showDraftedBanner={false}
      onToggleDrafted={() => {}}
      bg={effectiveBg}
      sortable={false}
      clickable={false}
      marginRight={0}
      width={140}
      minWidth={140}
      borderRadius={16}
      padding={8}
    >
      <CellContent
        label={label}
        name={player?.name ?? "—"}
        position={player?.position ?? " "}
        team={player?.team}
        imageUrl={player?.imageUrl}
        showDash={Boolean(player)}
        showImage
      />
    </BoardCell>
  );
}

function DraggableTeamCard({
  teamIndex,
  label,
  player,
  posColor,
  highlighted = false,
  allowRosterEditing = false,
  compactMode = false,
  compactLayout = "phone",
}: {
  teamIndex: number;
  label: string;
  player?: Player;
  posColor: (pos: Position) => string;
  highlighted?: boolean;
  allowRosterEditing?: boolean;
  compactMode?: boolean;
  compactLayout?: CompactLayout;
}) {
  const dragId = player ? buildDragId(teamIndex, player.id) : undefined;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId ?? `static:${teamIndex}:${label}`,
    disabled: !allowRosterEditing || !player,
    data: allowRosterEditing && player ? { teamIndex, playerId: player.id } : undefined,
  });

  return (
    <div
      ref={setNodeRef}
      {...(allowRosterEditing && player ? attributes : {})}
      {...(allowRosterEditing && player ? listeners : {})}
      style={{
        transform: allowRosterEditing && transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: allowRosterEditing && isDragging ? 0.35 : 1,
      }}
    >
      <PlayerChip
        label={label}
        player={player}
        posColor={posColor}
        highlighted={highlighted}
        draggableId={dragId}
        isDragging={false}
        compactMode={compactMode}
        compactLayout={compactLayout}
      />
    </div>
  );
}

function TeamStarterSlot({
  teamIndex,
  slotId,
  label,
  player,
  posColor,
  highlighted = false,
  allowRosterEditing = false,
  compactMode = false,
  compactLayout = "phone",
}: {
  teamIndex: number;
  slotId: string;
  label: string;
  player?: Player;
  posColor: (pos: Position) => string;
  highlighted?: boolean;
  allowRosterEditing?: boolean;
  compactMode?: boolean;
  compactLayout?: CompactLayout;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: buildSlotDropId(teamIndex, slotId), disabled: !allowRosterEditing });

  return (
    <div
      ref={setNodeRef}
      style={{
        borderRadius: compactMode ? 7 : 18,
        boxShadow: isOver ? "0 0 0 2px rgba(255,255,255,0.22)" : undefined,
        background: isOver ? "rgba(255,255,255,0.06)" : undefined,
      }}
    >
      <DraggableTeamCard
        teamIndex={teamIndex}
        label={label}
        player={player}
        posColor={posColor}
        highlighted={highlighted}
        allowRosterEditing={allowRosterEditing}
        compactMode={compactMode}
        compactLayout={compactLayout}
      />
    </div>
  );
}

function TeamBenchArea({
  teamIndex,
  bench,
  benchSlots,
  posColor,
  highlighted = false,
  allowRosterEditing = false,
  compactMode = false,
  compactLayout = "phone",
}: {
  teamIndex: number;
  bench: Player[];
  benchSlots: number;
  posColor: (pos: Position) => string;
  highlighted?: boolean;
  allowRosterEditing?: boolean;
  compactMode?: boolean;
  compactLayout?: CompactLayout;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: buildBenchDropId(teamIndex), disabled: !allowRosterEditing });
  const visibleBenchCount = Math.max(benchSlots, bench.length);

  return (
    <div
      ref={setNodeRef}
      style={{
        borderRadius: compactMode ? 7 : 18,
        padding: 0,
        boxShadow: isOver ? "0 0 0 2px rgba(255,255,255,0.22)" : undefined,
        background: isOver ? "rgba(255,255,255,0.06)" : undefined,
      }}
    >
      {Array.from({ length: visibleBenchCount }).map((_, benchIndex) => {
        const player = bench[benchIndex];
        const label = benchIndex === 0 ? "Bench" : `Bench ${benchIndex + 1}`;
        return (
        <div key={`${teamIndex}-bench-${benchIndex}-${player?.id ?? "empty"}`} style={{ marginBottom: compactMode ? -8 : 2 }}>
          <DraggableTeamCard
            teamIndex={teamIndex}
            label={label}
            player={player}
            posColor={posColor}
            highlighted={highlighted}
            allowRosterEditing={allowRosterEditing}
            compactMode={compactMode}
            compactLayout={compactLayout}
          />
        </div>
        );
      })}
    </div>
  );
}

export default function TeamsBoard(props: {
  teams: number;
  rounds: number;
  draftStyle: DraftStyle;
  draftSlots: (string | null)[];
  draftSlotTeamIndexes?: Array<number | null>;
  teamNames: string[];
  playersById: Record<string, Player>;
  posColor: (pos: Position) => string;
  starterConfig: StarterConfig;
  setStarterConfig: React.Dispatch<React.SetStateAction<StarterConfig>>;
  teamOverrides: TeamOverridesState;
  setTeamOverrides: React.Dispatch<React.SetStateAction<TeamOverridesState>>;
  fitToViewport?: boolean;
  scaleToViewport?: boolean;
  compactLayout?: CompactLayout;
  mobileViewOpen?: boolean;
  setMobileViewOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  allowRosterEditing?: boolean;
}) {
  const {
    teams,
    rounds,
    draftStyle,
    draftSlots,
    draftSlotTeamIndexes,
    teamNames,
    playersById,
    posColor,
    starterConfig,
    setStarterConfig,
    teamOverrides,
    setTeamOverrides,
    fitToViewport = false,
    scaleToViewport = false,
    compactLayout = "phone",
    mobileViewOpen: mobileViewOpenProp,
    setMobileViewOpen: setMobileViewOpenProp,
    allowRosterEditing = false,
  } = props;
  const isTabletCompact = compactLayout === "tablet";
  const compactWidth = isTabletCompact ? 91 : 72;
  const compactColumnGap = isTabletCompact ? 2 : 1;
  const compactHeaderMinHeight = isTabletCompact ? 16 : 14;
  const compactHeaderCardMinHeight = isTabletCompact ? 36 : 28;
  const compactHeaderFontSize = isTabletCompact ? 8 : 6;
  const compactTeamNameFontSize = isTabletCompact ? 11 : 9;
  const [activeDrag, setActiveDrag] = React.useState<{ teamIndex: number; player: Player } | null>(null);
  const { viewportRef, contentRef, touchHandlers } = useFitZoomViewport(false);
  const [contentScale, setContentScale] = React.useState(1);
  const [mobileViewOpenInternal, setMobileViewOpenInternal] = React.useState(false);
  const mobileViewOpen = mobileViewOpenProp ?? mobileViewOpenInternal;
  const setMobileViewOpen = setMobileViewOpenProp ?? setMobileViewOpenInternal;

  const visibleStarterSlots = React.useMemo(() => buildVisibleStarterSlots(starterConfig), [starterConfig]);
  const benchSlotCount = React.useMemo(
    () => Math.max(0, rounds - visibleStarterSlots.length),
    [rounds, visibleStarterSlots.length]
  );
  const nextPickingTeamIndex = React.useMemo(
    () => getNextPickingTeamIndex(draftSlots, teams, draftStyle, draftSlotTeamIndexes),
    [draftSlotTeamIndexes, draftSlots, teams, draftStyle]
  );
  const nextDraftDirection = React.useMemo(
    () => getNextDraftDirection(draftSlots, teams, draftStyle),
    [draftSlots, teams, draftStyle]
  );

  const teamPlayerIds = React.useMemo(() => {
    const grouped = Array.from({ length: teams }, () => [] as string[]);
    draftSlots.forEach((playerId, pickIndex) => {
      if (!playerId) return;
      const teamIndex = getTeamIndexForPick(pickIndex, teams, draftStyle, draftSlotTeamIndexes);
      grouped[teamIndex]?.push(playerId);
    });
    return grouped;
  }, [draftSlotTeamIndexes, draftSlots, teams, draftStyle]);

  React.useEffect(() => {
    setTeamOverrides((prev) => {
      const next: TeamOverridesState = {};
      let changed = false;

      teamPlayerIds.forEach((playerIds, teamIndex) => {
        const prevTeamOverrides = prev[teamIndex] ?? {};
        const playerIdSet = new Set(playerIds);
        const validSlotIds = new Set(visibleStarterSlots.map((slot) => slot.id));
        const filteredEntries = Object.entries(prevTeamOverrides).filter(([slotId, playerId]) => {
          if (!validSlotIds.has(slotId)) return false;
          if (!playerIdSet.has(playerId)) return false;
          const player = playersById[playerId];
          const slotKey = visibleStarterSlots.find((slot) => slot.id === slotId)?.key;
          return Boolean(player && slotKey && canFillSlot(slotKey, player));
        });

        if (filteredEntries.length > 0) {
          next[teamIndex] = Object.fromEntries(filteredEntries);
        }

        if (filteredEntries.length !== Object.keys(prevTeamOverrides).length) {
          changed = true;
        }
      });

      if (!changed && Object.keys(prev).length === Object.keys(next).length) return prev;
      return next;
    });
  }, [setTeamOverrides, teamPlayerIds, visibleStarterSlots, playersById]);

  const teamRosters = React.useMemo(
    () =>
      teamPlayerIds.map((playerIds, teamIndex) =>
        buildTeamRoster(playerIds, playersById, starterConfig, teamOverrides[teamIndex] ?? {})
      ),
    [teamPlayerIds, playersById, starterConfig, teamOverrides]
  );

  const selectStyle: React.CSSProperties = {
    borderRadius: fitToViewport ? 8 : 10,
    border: "1px solid var(--border-0)",
    background: "var(--panel-bg-2)",
    color: "var(--text-0)",
    padding: fitToViewport ? "2px 4px" : "6px 10px",
    outline: "none",
    fontWeight: 750,
    fontSize: fitToViewport ? 10 : 14,
  };

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      if (!allowRosterEditing) return;
      const parsed = parseDragId(String(event.active.id));
      if (!parsed) {
        setActiveDrag(null);
        return;
      }

      const player = playersById[parsed.playerId];
      if (!player) {
        setActiveDrag(null);
        return;
      }

      setActiveDrag({ teamIndex: parsed.teamIndex, player });
    },
    [allowRosterEditing, playersById]
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!allowRosterEditing) {
        setActiveDrag(null);
        return;
      }
      const active = parseDragId(String(event.active.id));
      const over = event.over ? parseDropId(String(event.over.id)) : null;
      setActiveDrag(null);

      if (!active || !over) return;
      if (active.teamIndex !== over.teamIndex) return;

      const roster = teamRosters[active.teamIndex];
      const activePlayer = playersById[active.playerId];
      if (!roster || !activePlayer) return;

      const sourceLocation = roster.playerLocation[active.playerId];
      if (!sourceLocation) return;

      const overSlotId = over.slotId;

      if (!over.isBench && overSlotId) {
        const targetSlot = visibleStarterSlots.find((slot) => slot.id === overSlotId);
        if (!targetSlot || !canFillSlot(targetSlot.key, activePlayer)) return;

        const targetPlayer = roster.slotAssignments[overSlotId];
        const sourceSlotId = sourceLocation.kind === "slot" ? sourceLocation.slotId : undefined;

        setTeamOverrides((prev) => {
          const nextTeamOverrides = { ...(prev[active.teamIndex] ?? {}) };

          Object.keys(nextTeamOverrides).forEach((slotId) => {
            if (nextTeamOverrides[slotId] === active.playerId) delete nextTeamOverrides[slotId];
          });

          nextTeamOverrides[overSlotId] = active.playerId;

          if (sourceSlotId && sourceSlotId !== overSlotId) {
            delete nextTeamOverrides[sourceSlotId];

            if (targetPlayer && targetPlayer.id !== active.playerId) {
              nextTeamOverrides[sourceSlotId] = targetPlayer.id;
            }
          }

          return {
            ...prev,
            [active.teamIndex]: nextTeamOverrides,
          };
        });

        return;
      }

      if (over.isBench) {
        if (sourceLocation.kind !== "slot") return;

        setTeamOverrides((prev) => {
          const nextTeamOverrides = { ...(prev[active.teamIndex] ?? {}) };
          delete nextTeamOverrides[sourceLocation.slotId];

          return {
            ...prev,
            [active.teamIndex]: nextTeamOverrides,
          };
        });
      }
    },
    [allowRosterEditing, playersById, setTeamOverrides, teamRosters, visibleStarterSlots]
  );

  React.useLayoutEffect(() => {
    if (!scaleToViewport) {
      setContentScale(1);
      return;
    }

    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const update = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const measuredWidth = content.getBoundingClientRect().width;
      const naturalWidth = contentScale > 0 ? measuredWidth / contentScale : measuredWidth;
      if (viewportRect.width <= 0 || naturalWidth <= 0) return;
      if (naturalWidth <= 0) return;

      const minScale = 0.35;
      const maxScale = 1.45;
      const fitPadding = fitToViewport ? 8 : 40;
      const nextScale = Math.max(minScale, Math.min(maxScale, (viewportRect.width - fitPadding) / naturalWidth));
      setContentScale((prev) => (Math.abs(prev - nextScale) < 0.01 ? prev : nextScale));
    };

    update();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => update());
    ro.observe(viewport);
    ro.observe(content);
    return () => ro.disconnect();
  }, [scaleToViewport, fitToViewport, teams, draftSlots, starterConfig, contentRef, viewportRef, contentScale]);

  return (
    <DndContext onDragStart={allowRosterEditing ? handleDragStart : undefined} onDragEnd={allowRosterEditing ? handleDragEnd : undefined}>
      <div style={{ display: "flex", flexDirection: "column", gap: fitToViewport ? 0 : 12, width: "100%", position: "relative" }}>
        {!fitToViewport ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              padding: 12,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.10)",
              outline: "1px solid rgba(0,0,0,0.18)",
              background: "var(--panel-bg)",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {STARTER_ORDER.map((slot) => (
              <label
                key={slot}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "var(--text-0)",
                  minWidth: 0,
                }}
              >
                <span style={{ whiteSpace: "nowrap", lineHeight: 1 }}>{STARTER_LABELS[slot]}</span>
                <select
                  value={starterConfig[slot]}
                  onChange={(e) =>
                    setStarterConfig((prev) => ({
                      ...prev,
                      [slot]: Number(e.target.value),
                    }))
                  }
                  style={selectStyle}
                  aria-label={`${STARTER_LABELS[slot]} starters`}
                >
                  {Array.from({ length: 7 }, (_, idx) => idx).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ) : null}

        <div
          ref={viewportRef}
          {...touchHandlers}
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            overflowY: "visible",
            touchAction: fitToViewport ? "pan-x pan-y" : undefined,
          }}
        >
          <div
            ref={contentRef}
            style={{
              width: "fit-content",
              zoom: contentScale,
            }}
          >
            <div style={{ display: "flex", overflowX: "visible", paddingTop: 0, paddingRight: fitToViewport ? 4 : 0, paddingBottom: 0, paddingLeft: 0 }}>
              {Array.from({ length: teams }).map((_, teamIndex) => {
            const teamName = teamNames[teamIndex]?.trim() || `Team ${teamIndex + 1}`;
            const roster = teamRosters[teamIndex];
            const isNextPickingTeam = nextPickingTeamIndex === teamIndex;
            const showLeftArrow = isNextPickingTeam && nextDraftDirection === "left";
            const showRightArrow = isNextPickingTeam && nextDraftDirection === "right";

            return (
              <div
                key={`${teamIndex}-${teamName}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: fitToViewport ? compactColumnGap : 4,
                  borderRadius: fitToViewport ? 7 : 18,
                  padding: fitToViewport ? 0 : 0,
                }}
              >
                <div
                  style={{
                    width: fitToViewport ? compactWidth : 140,
                    minWidth: fitToViewport ? compactWidth : 140,
                    minHeight: fitToViewport ? compactHeaderMinHeight : 18,
                    marginRight: fitToViewport ? compactColumnGap : 0,
                    marginBottom: fitToViewport ? 2 : 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: fitToViewport ? 2 : 6,
                    fontSize: fitToViewport ? compactHeaderFontSize : 11,
                    fontWeight: 900,
                    letterSpacing: fitToViewport ? 0.2 : 0.6,
                    textTransform: "uppercase",
                    color: "rgba(255, 235, 140, 0.98)",
                    whiteSpace: "nowrap",
                    visibility: isNextPickingTeam ? "visible" : "hidden",
                  }}
                >
                  {showLeftArrow ? <span aria-hidden>←</span> : null}
                  <span>On the Clock</span>
                  {showRightArrow ? <span aria-hidden>→</span> : null}
                </div>

                <div
                  style={{
                    boxSizing: "border-box",
                    width: fitToViewport ? compactWidth : 140,
                    minWidth: fitToViewport ? compactWidth : 140,
                    height: fitToViewport ? compactHeaderCardMinHeight : 48,
                    minHeight: fitToViewport ? compactHeaderCardMinHeight : 48,
                    marginRight: fitToViewport ? compactColumnGap : 0,
                    marginBottom: fitToViewport ? 2 : 2,
                    borderRadius: fitToViewport ? 7 : 16,
                    border: isNextPickingTeam
                      ? "1px solid rgba(255,215,0,0.55)"
                      : "1px solid rgba(255,255,255,0.10)",
                    outline: isNextPickingTeam
                      ? "1px solid rgba(255,215,0,0.32)"
                      : "1px solid rgba(0,0,0,0.18)",
                    background: isNextPickingTeam ? "rgba(255, 215, 0, 0.18)" : "var(--panel-bg)",
                    padding: fitToViewport ? 0 : 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      minWidth: 0,
                      fontSize: fitToViewport ? compactTeamNameFontSize : 15,
                      fontWeight: fitToViewport ? 800 : 900,
                      color: "var(--text-0)",
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      padding: 0,
                    }}
                    title={teamName}
                  >
                    {teamName}
                  </div>
                </div>

                {visibleStarterSlots.map((slot) => (
                  <div key={`${teamIndex}-${slot.id}`} style={{ marginBottom: fitToViewport ? -8 : 2 }}>
                    <TeamStarterSlot
                      teamIndex={teamIndex}
                      slotId={slot.id}
                      label={slot.label}
                      player={roster?.slotAssignments[slot.id]}
                      posColor={posColor}
                      highlighted={isNextPickingTeam}
                      allowRosterEditing={allowRosterEditing}
                      compactMode={fitToViewport}
                      compactLayout={compactLayout}
                    />
                  </div>
                ))}

                <TeamBenchArea
                  teamIndex={teamIndex}
                  bench={roster?.bench ?? []}
                  benchSlots={benchSlotCount}
                  posColor={posColor}
                  highlighted={isNextPickingTeam}
                  allowRosterEditing={allowRosterEditing}
                  compactMode={fitToViewport}
                  compactLayout={compactLayout}
                />
              </div>
            );
              })}
            </div>
          </div>
        </div>

        {fitToViewport && mobileViewOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2200,
              background: "rgba(4,7,18,0.46)",
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isTabletCompact ? 20 : 14,
            }}
            onPointerDown={() => setMobileViewOpen(false)}
          >
            <div
              style={{
                width: isTabletCompact ? "min(360px, calc(100vw - 40px))" : "min(320px, calc(100vw - 28px))",
                padding: isTabletCompact ? 14 : 12,
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(18,18,22,0.98)",
                boxShadow: "0 24px 50px rgba(0,0,0,0.42)",
                display: "grid",
                gap: 10,
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Rosters View
                </div>
                <button
                  type="button"
                  onClick={() => setMobileViewOpen(false)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "var(--text-0)",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                Position Counts
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: isTabletCompact ? 8 : 6,
                }}
              >
                {STARTER_ORDER.map((slot) => (
                  <label
                    key={slot}
                    style={{
                      display: "grid",
                      gap: 6,
                      padding: isTabletCompact ? "10px 12px" : "9px 10px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.04)",
                      color: "var(--text-0)",
                    }}
                  >
                    <span style={{ fontSize: isTabletCompact ? 12 : 11, fontWeight: 800, lineHeight: 1 }}>
                      {MOBILE_STARTER_LABELS[slot]}
                    </span>
                    <select
                      value={starterConfig[slot]}
                      onChange={(e) =>
                        setStarterConfig((prev) => ({
                          ...prev,
                          [slot]: Number(e.target.value),
                        }))
                      }
                      style={selectStyle}
                      aria-label={`${STARTER_LABELS[slot]} starters`}
                    >
                      {Array.from({ length: 7 }, (_, idx) => idx).map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {allowRosterEditing ? (
      <DragOverlay>
        {activeDrag ? (
          <PlayerChip
            label={activeDrag.player.position}
            player={activeDrag.player}
            posColor={posColor}
            isDragging
            compactMode={fitToViewport}
            compactLayout={compactLayout}
          />
        ) : null}
      </DragOverlay>
      ) : null}
    </DndContext>
  );
}
